import React, { useState } from 'react';
import './AlertSteps.css';
import ReportStepQuery from './ReportStepQuery';
import AlertSpecProblems from './AlertSpecProblems';
import AlertMatchCount from './AlertMatchCount';
import AlertConversationPanel from './AlertConversationPanel';
import AlertThresholdFields from './AlertThresholdFields';
import AlertEditOpener from './AlertEditOpener';
import { useAlertConversation } from '../useAlertConversation';
import { describeDelivery } from '../alertDelivery';

/**
 * Step 2, conversational (PRD-1381).
 *
 * Reuses `ReportStepQuery` exactly as the form version does, and replaces only
 * the query control through the slot that step exposes. The alert name, the
 * dataset multi-select with its inline validation and the saved-query dropdown
 * are therefore the same components as everywhere else and cannot drift.
 *
 * **The threshold fields are gone here, and that is the point.** In the form
 * they are how the threshold stays out of the generated SQL; in the
 * conversation, the agent holds it as a slot and composes a sentence without it.
 * Measured: with the threshold in the sentence, 0 of 72 attempts produced a
 * correct alert; with it composed out, 19 of 51 did, and every accepted
 * definition kept it out of the query.
 *
 * **Falls back to the form when the agent is unreachable.** Not a silent
 * degrade — the author is told once, and then gets a step 2 that works.
 *
 * **Editing is a different screen in the same step (PRD-1382).** Creating, the
 * threshold is deliberately absent: it must stay out of the sentence the agent
 * writes. Editing, the threshold already exists and is stored, and the ticket
 * wants moving it to be *instant* — "going through a 43-second round trip to
 * change a 3 to a 5 is both slow and unsafe". So an edit gets the threshold
 * controls back, directly, with no model call; the conversation stays for
 * changing what is measured.
 *
 * Without that, the only route to any edit would be a conversation that
 * regenerates the query, which would make every edit a measurement change —
 * disarming the alert and demanding a re-verification to move a number. The
 * instant path would exist on the server and be unreachable from the console.
 */
/**
 * Whether the form still holds the comparison the latest build derived.
 *
 * The threshold and comparator are kept OUT of the query on purpose — that is
 * the whole of PRD-1380 — so a rebuild after "make it 5" can return identical
 * SQL with a different comparison. Matching on the query alone left the create
 * card "ready" over the old threshold, and Create saved 3 while the chat and the
 * preview said 5. Raised in review of PRD-1447.
 *
 * Compares exactly the fields `handleBuild` copies, in the form's own shape.
 */
const holdsComparison = (alert, built) => (
  (alert?.shape ?? '') === (built.shape ?? '')
  && (alert?.subjectColumn ?? '') === (built.subject_column ?? '')
  && (alert?.measureColumn ?? '') === (built.measure_column ?? '')
  && (alert?.comparator ?? '') === (built.comparator ?? '')
  && String(alert?.threshold ?? '') === String(built.threshold ?? '')
);

const AlertStepConversationView = ({
  conversation,
  formData,
  onSetFormData,
  dryRun,
  problems = [],
  notReadyReason,
  FallbackStep = null,
  isEditMode = false,
  onCreateNow = null,
  creating = false,
  createProblems = [],
  onEditDelivery = null,
}) => {
  // The author chose to finish a capped conversation in the form (PRD-1447).
  // Local, so leaving the step and coming back offers the choice again rather
  // than stranding them in the form with the conversation hidden.
  const [finishingInForm, setFinishingInForm] = useState(false);

  // Built, but the agent would not say which projection is which. Not a failure
  // — it declines rather than guessing — but the author still has to finish it.
  const comparisonUnresolved = Boolean(conversation.definition)
    && !conversation.definition.comparison;

  // The built alert has been taken into the form — Build was pressed for *this*
  // definition. Derived from the form rather than held as a flag, so a later
  // turn that rebuilds a different alert does not inherit "ready" from the last.
  //
  // The query alone is not enough to say "this one": see `holdsComparison`. An
  // unresolved comparison has none to match — the author sets it by hand in the
  // fields this step shows for exactly that case — so only the query is checked.
  const builtComparison = conversation.definition?.comparison;

  // **Nothing to warn about until there is an alert** (PRD-1463). On a fresh
  // conversation the "Before you continue" list and the live count's not-ready
  // banner both named fields the author had not been asked about yet, so the
  // first screen opened on warnings. They return once a build exists, which is
  // when they say something the author can act on. Edit mode starts with one.
  const hasAlert = isEditMode || Boolean(conversation.definition);
  const applied = !isEditMode
    && Boolean(conversation.definition?.sql)
    && formData.alert?.sql === conversation.definition.sql
    && (!builtComparison || holdsComparison(formData.alert, builtComparison));

  // **Finishing a capped conversation in the form** (PRD-1447). The cap used to
  // be a dead end: "build what you have, or start again", with no built alert
  // to build and no way to start again. Now the form takes over, seeded with
  // what was agreed:
  //
  //  - the question is the agent's COMPOSED sentence, not the author's words.
  //    Theirs carry the threshold ("3 or more…"), and a threshold in the
  //    question becomes one in the SQL — the failure the conversation exists to
  //    avoid. The agent returns it threshold-free on a capped turn.
  //  - the threshold and comparator go to their own fields, which is where the
  //    form keeps them out of the query for the same reason.
  //
  // Nothing already in the form is cleared: a seed only fills what the
  // conversation actually established.
  const handleFinishInForm = () => {
    const slots = conversation.slots || {};
    onSetFormData((prev) => ({
      ...prev,
      reportQuery: conversation.sentence || prev.reportQuery || '',
      alert: {
        ...(prev.alert ?? {}),
        ...(slots.threshold != null ? { threshold: String(slots.threshold), thresholdKind: 'value' } : {}),
        ...(slots.comparator ? { comparator: slots.comparator } : {}),
      },
    }));
    setFinishingInForm(true);
  };

  // Start again: a fresh conversation, the form left as it is. Whatever the
  // author typed into the name or datasets is theirs and survives.
  const handleStartAgain = () => {
    setFinishingInForm(false);
    conversation.reset?.();
  };

  if (finishingInForm && FallbackStep) {
    return (
      <div className="alert-step-content">
        <p className="report-helper" role="status">
          Finishing in the form with what the conversation agreed.{' '}
          <button type="button" className="resui-btn resui-btn--secondary" onClick={handleStartAgain}>
            Start a new conversation
          </button>
        </p>
        <FallbackStep
          formData={formData}
          onSetFormData={onSetFormData}
          dryRun={dryRun}
          problems={problems}
          notReadyReason={notReadyReason}
        />
      </div>
    );
  }

  // The agent is gone. Hand the author the form rather than a dead screen — it
  // is the same step 2 the flag-off path renders, so nothing is lost but the
  // conversation.
  if (conversation.agentUnavailable && FallbackStep) {
    return (
      <div className="alert-step-content">
        <p className="report-helper report-helper-warn" role="alert">
          The alert assistant is unavailable, so this step has switched to the form. Everything
          still works — describe the query and set the threshold yourself.
        </p>
        <FallbackStep
          formData={formData}
          onSetFormData={onSetFormData}
          dryRun={dryRun}
          problems={problems}
          notReadyReason={notReadyReason}
        />
      </div>
    );
  }

  const handleBuild = () => {
    const definition = conversation.definition;
    if (!definition) return;

    // **The comparison the agent derived, or nothing.**
    //
    // Without this the step could never be completed: the wizard validates that
    // a subject column, a measure column and a threshold are set, and this
    // screen deliberately has no controls for any of them — the threshold is a
    // slot precisely so it stays out of the generated SQL. So the author was
    // told to fill in three fields that are not on the page.
    //
    // The agent sends it only when the query's shape settles the roles
    // unambiguously, so an absent comparison means "it could not tell", not
    // "there isn't one". Leaving the fields alone in that case puts the author
    // back where they were rather than storing a threshold against a guess.
    // **Every field the shape reads, not only the ones a threshold reads.**
    //
    // This copied six fields, which was the whole comparison while `threshold`
    // was the only shape the agent could produce. It can now produce a
    // `run_length` — "below 85 on 3 or more consecutive days" — and that shape
    // reads three more. Copying the shape without them set the wizard to
    // `run_length` and left `periodColumn`, `periodUnit` and `runLength` empty,
    // so `validateAlertSpecInputs` reported three problems and the step-2 gate
    // never opened: the author was told to fill in three fields that are not on
    // this page. Which is the exact failure this block was written to fix, one
    // shape later.
    //
    // Cleared rather than omitted when the shape does not use them, matching
    // `AlertThresholdFields`, which blanks them on a shape change for the same
    // reason: a value left over from an earlier build would be sent on save.
    // The key names and the string-vs-empty convention are the edit path's, in
    // `alertPayload.js` — one mapping from a stored comparison to form state,
    // not two that can disagree.
    const comparison = definition.comparison
      ? {
        shape: definition.comparison.shape,
        subjectColumn: definition.comparison.subject_column,
        measureColumn: definition.comparison.measure_column,
        comparator: definition.comparison.comparator,
        threshold: String(definition.comparison.threshold),
        thresholdKind: 'value',
        periodColumn: definition.comparison.period_column ?? '',
        periodUnit: definition.comparison.period_unit ?? '',
        runLength: definition.comparison.run_length != null
          ? String(definition.comparison.run_length)
          : '',
      }
      : {};

    // The stored document is the far side's, relayed untouched. Reshaping it
    // here is how a previewed definition and a saved one come to differ.
    onSetFormData((prev) => {
      // **The agent's name, unless the author has made it theirs** (PRD-1447).
      // The name is the one required field with no default, so without this a
      // conversation could build an alert and still not save it. But a name the
      // author typed is theirs, and overwriting it on Build would silently undo a
      // choice made on this same screen.
      //
      // "Empty" was the first test, and it was not enough: after the first Build
      // filled the field it was no longer empty, so a rebuild at a new threshold
      // kept the old name — which carries the threshold by design, so the alert
      // was saved as "…at least 3…" while firing at 5. Raised in review. The
      // field is still ours while it holds exactly what we last put there.
      const seeded = prev.seededAutomationName;
      const nameIsOurs = !prev.automationName?.trim() || (Boolean(seeded) && prev.automationName === seeded);
      const proposed = conversation.suggestedName;
      return {
        ...prev,
        reportQuery: conversation.sentence,
        automationName: nameIsOurs && proposed ? proposed : (prev.automationName || ''),
        // Form state only: `buildCreatePayload` and `buildUpdatePayload` name
        // the fields they send, so this never reaches the server. An edit loads
        // a stored name and no seed, so the stored name is never "ours".
        seededAutomationName: nameIsOurs && proposed ? proposed : seeded,
        alert: {
        ...(prev.alert ?? {}),
        sql: definition.sql,
        // The reference the payloads send instead of the query (PRD-1438).
        queryDraftId: definition.draftId ?? null,
        window: definition.window ?? null,
        resultColumns: definition.columns,
        previewRows: definition.rows,
        // **snake_case, because `definition` is an untouched passthrough.**
        // resapi models the *form* path's response and aliases `match_count` to
        // `matchCount` there; the conversation's `definition` is relayed as a raw
        // dict, so it arrives in the far side's own shape — as its sibling
        // `comparison.subject_column` on the line above already shows. Reading
        // `matchCount` here yielded undefined, so the preview panel reported the
        // count of rows it was displaying rather than the uncapped total, which
        // is the number an author calibrates a threshold against. Pre-existing;
        // found in review of this branch.
        previewRowCount: definition.match_count ?? definition.matchCount,
        ...comparison,
        },
      };
    });
  };

  // The datasets the conversation reads, named in the chat box. Built from the
  // list the report step already loaded, and removed through the same toggle as
  // its dataset picker, so the tags and the picker are one piece of state shown
  // twice rather than two to keep in step. (This was an empty list until it was
  // asked for: an earlier version passed a field that did not exist, and
  // rendering nothing was better than an empty row.)
  const tagsFor = (datasets, selectedIds) => selectedIds
    .map((id) => datasets.find((ds) => String(ds.id) === String(id)))
    .filter(Boolean)
    .map((ds) => ({ id: String(ds.id), name: ds.name }));

  return (
    <div className="alert-step-content">
      {isEditMode && <AlertEditOpener summary={formData.alertSummary} />}

      <ReportStepQuery
        formData={formData}
        onSetFormData={onSetFormData}
        title="Query and threshold"
        nameLabel="Alert name"
        namePlaceholder="Name this alert"
        queryLabel="What should this alert watch for?"
        // The conversation shows what the built alert matches itself; the
        // report's REGENERATE and Response Preview would be leftovers here.
        showRunAndPreview={false}
        // Picking a past Ask ResAI question would fill a query text the
        // conversation never reads, and swap the datasets mid-conversation.
        showSavedQuery={false}
        showDatasetRequiredNote={false}
        queryControl={({ datasets, selectedDatasetIds, onRemoveDataset }) => (
          <AlertConversationPanel
            messages={conversation.messages}
            pending={conversation.pending}
            capped={conversation.capped}
            settled={conversation.settled}
            definition={conversation.definition}
            error={conversation.error}
            retryAfter={conversation.retryAfter}
            staleDefinition={conversation.staleDefinition}
            datasetsChangedMidConversation={conversation.datasetsChangedMidConversation}
            datasetTags={tagsFor(datasets, selectedDatasetIds)}
            needsDataset={selectedDatasetIds.length === 0}
            onRemoveDataset={onRemoveDataset}
            dataFreshness={conversation.definition?.dataFreshness ?? null}
            volumeGuardMaxMatches={formData.volumeGuardMaxMatches}
            // The cap the server actually applied, once there is a result to
            // read it from. Authoritative over anything computed here.
            servedMaxMatches={dryRun?.result?.volumeGuard?.threshold ?? null}
            onSend={conversation.send}
            onBuild={handleBuild}
            onStartAgain={handleStartAgain}
            onFinishInForm={FallbackStep ? handleFinishInForm : null}
          />
        )}
      />

      {/*
        The agent built a query but could not say which projection is the subject
        and which is the measure — two groupings, say. Declining beats guessing,
        but declining used to leave the author in the dead end this screen exists
        to remove: a real preview above, and three validation errors below naming
        controls that are not on the page.

        So the controls appear, populated from the columns the build just
        returned — the author picks from what their query actually projects
        rather than being told to find them. The conversation and the preview
        both stay. Raised in review of #195.
      */}
      {!isEditMode && comparisonUnresolved && (
        <p className="report-helper report-helper-warn" role="alert">
          The alert was built, but it could not tell which column identifies the subject and which
          holds the value to compare — the query returns more than one of each. Choose them below
          and set the threshold; the rest is done.
        </p>
      )}

      {/*
        The instant path when editing. No model call — the author moves the
        number and saves, and the server writes the stored query back untouched.
      */}
      {(isEditMode || comparisonUnresolved) && (
        <AlertThresholdFields formData={formData} onSetFormData={onSetFormData} />
      )}

      {hasAlert && <AlertSpecProblems problems={problems} heading="Before you continue" />}

      {/*
        **Create from here, without the last two steps** (PRD-1447).

        Once a built alert has been applied, every remaining field already has a
        default that passes — email, warning, daily at 08:00, sent to the author —
        and the name is seeded from the agent. So Recipients and Schedule were
        steps the author clicked through to change nothing.

        They are not removed. The delivery is stated here in words, so a default
        the author never chose is at least one they have seen, and "Change"
        opens the Recipients step for anyone who wants it. The wizard's own Next
        still leads through both.

        Create only, not edit: an edit can be a measurement change, which the
        wizard deliberately holds behind an acknowledgement on this step, and a
        shortcut past that would defeat it.
      */}
      {applied && onCreateNow && (
        <section className="alert-create-now" aria-labelledby="alert-create-now-heading">
          <h4 id="alert-create-now-heading" className="report-label">Ready to create</h4>
          <p className="report-helper">{describeDelivery(formData)}</p>
          {onEditDelivery && (
            <button type="button" className="resui-btn resui-btn--secondary" onClick={onEditDelivery}>
              Change who it goes to or when
            </button>
          )}
          <AlertSpecProblems problems={createProblems} heading="Before you create it" />
          <button
            type="button"
            className="reports-wizard-btn reports-wizard-btn-primary"
            onClick={onCreateNow}
            disabled={creating || createProblems.length > 0}
          >
            {creating ? 'Creating…' : 'Create alert'}
          </button>
        </section>
      )}

      {hasAlert && (
        <AlertMatchCount
          result={dryRun?.result}
          error={dryRun?.error}
          loading={dryRun?.loading}
          ready={dryRun?.ready}
          notReadyReason={notReadyReason}
        />
      )}
    </div>
  );
};

/**
 * Owns a conversation for a host that does not pass one in.
 *
 * Kept as its own component so the hook is never called conditionally: a host
 * that passes `conversation` renders the view directly and this never mounts.
 */
const OwnedConversationStep = (props) => {
  const conversation = useAlertConversation({ datasetIds: props.formData?.reportDatasetIds ?? [] });
  return <AlertStepConversationView {...props} conversation={conversation} />;
};

/**
 * **The wizard should pass `conversation`, and does** (PRD-1447).
 *
 * The hook's docstring has always said the wizard keeps the conversation across
 * step changes. It did not: this step called the hook itself, and the wizard
 * renders only the current step, so leaving step 2 unmounted it and took the
 * transcript and the built alert with it. Nobody noticed while step 2 was a dead
 * end you only left forwards. "Change who it goes to or when" made it matter —
 * it sends the author to Recipients, and on the way back the conversation and
 * the create card were gone. Raised in review.
 *
 * So the wizard owns it and hands it down. A standalone render still works.
 */
const AlertStepConversation = (props) => (
  props.conversation
    ? <AlertStepConversationView {...props} />
    : <OwnedConversationStep {...props} />
);

export default AlertStepConversation;
